/**
 * Tiny IndexedDB-backed cache for the full Lorcana card catalog.
 *
 * The catalog changes infrequently (≈ once per set release), so persisting it
 * locally lets cold loads of the Library/Binder render instantly while a
 * background fetch revalidates the data.
 */

const DB_NAME = 'lorescout-cache';
const STORE = 'kv';
const DB_VERSION = 1;

/** Bump this whenever the on-disk shape changes. */
export const CARDS_CACHE_VERSION = 1;
/** Refresh in the background after this many ms (default 6h). */
export const CARDS_CACHE_FRESH_MS = 6 * 60 * 60 * 1000;

type Entry<T> = { v: number; t: number; data: T };

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('No IndexedDB'));
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  try {
    const db = await openDb();
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
}

async function idbSet<T>(key: string, value: T): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export async function readCachedCards<T>(key: string): Promise<{ data: T; staleMs: number } | null> {
  const entry = await idbGet<Entry<T>>(key);
  if (!entry || entry.v !== CARDS_CACHE_VERSION) return null;
  return { data: entry.data, staleMs: Date.now() - entry.t };
}

export async function writeCachedCards<T>(key: string, data: T): Promise<void> {
  await idbSet<Entry<T>>(key, { v: CARDS_CACHE_VERSION, t: Date.now(), data });
}
